// ============================================================
// MyGene — DetailDrawer
// Standardized right-side drawer for all detail/edit views.
// Enforces consistent header, error alert, tabs, and footer.
//
// Usage:
//
//   <DetailDrawer
//     open={isOpen}
//     onClose={handleClose}
//     title="Patient Name"
//     subtitle="patient@email.com"
//     avatar={<Avatar icon={<UserOutlined />} />}
//     headerActions={<Button type="primary" onClick={save}>Save</Button>}
//     tabs={tabItems}
//     activeTab={activeTab}
//     onTabChange={setActiveTab}
//     errorMessages={errors}
//     onClearErrors={() => setErrors([])}
//     footer={<Button onClick={resetPassword}>Reset Password</Button>}
//   />
//
// Width guidance:
//   "70vw" — standard (user management, roles, simple forms)
//   "85vw" — data-heavy (patient records, sample tracking)
// ============================================================

import React from "react";
import { Drawer, Tabs, Alert, Avatar, Typography, Grid } from "antd";
import type { TabsProps } from "antd";
import InlineLoading from "./InlineLoading";

const { useBreakpoint } = Grid;

const { Title, Text } = Typography;

export interface DetailDrawerProps {
	// ── Core
	open: boolean;
	onClose: () => void;
	/** Default "70vw". Use "85vw" for data-heavy views. */
	width?: string | number;

	// ── Header (left side)
	/** Avatar or icon element rendered left of the title */
	avatar?: React.ReactNode;
	/** Primary heading — string or ReactNode */
	title?: React.ReactNode;
	/** Secondary line below the title */
	subtitle?: React.ReactNode;
	/** Extra element below subtitle (e.g. save-status badge) */
	titleMeta?: React.ReactNode;

	// ── Header (right side)
	/** Buttons/controls on the right side of the header */
	headerActions?: React.ReactNode;

	/**
	 * Tone of the header background/text. 
	 */
	headerTone?: "default" | "primary";

	// ── Body content
	/** Pass tab items to render a <Tabs> inside the body */
	tabs?: TabsProps["items"];
	activeTab?: string;
	onTabChange?: (key: string) => void;
	/** Used instead of tabs when there is no tab structure */
	children?: React.ReactNode;

	// ── Error display
	/** When non-empty, renders an error Alert at the top of the body */
	errorMessages?: string[];
	onClearErrors?: () => void;

	// ── Loading
	/** Replaces body content with InlineLoading when true */
	loading?: boolean;
	loadingMessage?: string;

	// ── Footer
	/** Rendered in the Drawer footer, right-aligned */
	footer?: React.ReactNode;

	/**
	 * Remove body padding and overflow restriction.
	 * Use for full-canvas builder drawers that manage their own padding/scroll.
	 */
	noPadding?: boolean;

	/**
	 * Controls how tabs are rendered.
	 * "top"     — standard horizontal Ant Design Tabs (default)
	 * "sidebar" — vertical left-rail navigation, content rendered on the right
	 */
	tabLayout?: "top" | "sidebar";

	/**
	 * Optional persistent panel rendered to the right of the main content (sidebar mode only).
	 * On small screens this panel is hidden; the parent should provide a toggle mechanism.
	 */
	rightPanel?: React.ReactNode;
	/** Width of the right panel in pixels. Defaults to 320. */
	rightPanelWidth?: number;
}

const DetailDrawer: React.FC<DetailDrawerProps> = ({
	open,
	onClose,
	width = "70vw",

	avatar,
	title,
	subtitle,
	titleMeta,
	headerActions,
	headerTone = "default",

	tabs,
	activeTab,
	onTabChange,
	children,

	errorMessages = [],
	onClearErrors,

	loading = false,
	loadingMessage,

	footer,
	noPadding = false,
	tabLayout = "top",
	rightPanel,
	rightPanelWidth = 320,
}) => {
	const screens = useBreakpoint();
	const showRightPanel = rightPanel && screens.lg;
	// ── Header ─────────────────────────────────────────────────
	const drawerTitle = (
		<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", gap: 12 }}>
			{/* Left: avatar + title/subtitle */}
			<div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1, minWidth: 0 }}>
				{avatar}
				<div style={{ flex: 1, minWidth: 0 }}>
					{typeof title === "string" && title ? (
						<Title level={5} style={{ margin: 0, lineHeight: 1.3 }}>
							{title}
						</Title>
					) : (
						title || null
					)}
					{subtitle && (
						<Text type="secondary" style={{ fontSize: 12, display: "block" }}>
							{subtitle}
						</Text>
					)}
					{titleMeta && <div style={{ marginTop: 3 }}>{titleMeta}</div>}
				</div>
			</div>

			{/* Right: action buttons */}
			{headerActions && (
				<div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, paddingTop: 2 }}>
					{headerActions}
				</div>
			)}
		</div>
	);

	// ── Footer ─────────────────────────────────────────────────
	const drawerFooter = footer ? (
		<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
			{footer}
		</div>
	) : undefined;

	// ── Body ───────────────────────────────────────────────────
	const errorAlert = errorMessages.length > 0 ? (
		<Alert
			message="Errors"
			description={
				<ul style={{ margin: 0, paddingLeft: 20 }}>
					{errorMessages.map((msg, idx) => (
						<li key={idx}>{msg}</li>
					))}
				</ul>
			}
			type="error"
			closable
			onClose={onClearErrors}
		/>
	) : null;

	const isSidebar = tabLayout === "sidebar" && !!tabs;

	const body = isSidebar ? (
		<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
			{errorAlert && (
				<div style={{ padding: "16px 20px 0" }}>{errorAlert}</div>
			)}
			{loading ? (
				<div style={{ flex: 1, padding: 16 }}>
					<InlineLoading message={loadingMessage} />
				</div>
			) : (
				<div style={{ display: "flex", flex: 1, minHeight: 0 }}>
					{/* Sidebar nav */}
					<nav
						style={{
							width: 200,
							flexShrink: 0,
							borderRight: "1px solid var(--mg-border)",
							background: "var(--mg-bg-subtle)",
							overflowY: "auto",
							paddingTop: 8,
							paddingBottom: 8,
						}}
					>
						{tabs.map((tab) => {
							const key = tab.key as string;
							if (key.startsWith("__divider_")) {
								return (
									<div
										key={key}
										style={{
											height: 1,
											background: "var(--mg-border)",
											margin: "6px 12px",
											pointerEvents: "none",
										}}
									/>
								);
							}
							// Group heading — a non-clickable label, never selectable as
							// activeTab. Mirrors `.mg-nav-section-heading` in Sidebar.css so
							// the drawer rail and the app sidebar read as the same rail.
							if (key.startsWith("__heading_")) {
								return (
									<div
										key={key}
										style={{
											padding: "12px 16px 4px",
											fontSize: 10,
											fontWeight: 600,
											letterSpacing: "0.08em",
											textTransform: "uppercase",
											color: "var(--mg-text-secondary)",
											opacity: 0.5,
											pointerEvents: "none",
											userSelect: "none",
										}}
									>
										{tab.label}
									</div>
								);
							}
							const isActive = activeTab === key;
							return (
								<div
									key={key}
									onClick={() => onTabChange?.(key)}
									style={{
										padding: "7px 16px",
										cursor: "pointer",
										fontSize: 12,
										lineHeight: 1.4,
										color: isActive ? "var(--mg-primary)" : "var(--mg-text)",
										background: isActive ? "var(--mg-primary-bg)" : "transparent",
										borderRight: isActive ? "2px solid var(--mg-primary)" : "2px solid transparent",
										fontWeight: isActive ? 600 : 400,
										transition: "background 0.15s, color 0.15s",
										userSelect: "none",
									}}
								>
									{tab.label}
								</div>
							);
						})}
					</nav>
					{/* Content area */}
					<div style={{ flex: 1, overflowY: "auto", padding: 16, minWidth: 0 }}>
						{tabs.find((t) => t.key === activeTab)?.children}
					</div>
					{/* Right panel — always visible on large screens */}
					{showRightPanel && (
						<div
							style={{
								width: rightPanelWidth,
								flexShrink: 0,
								borderLeft: "1px solid var(--mg-border)",
								overflowY: "auto",
								display: "flex",
								flexDirection: "column",
							}}
						>
							{rightPanel}
						</div>
					)}
				</div>
			)}
		</div>
	) : (
		<>
			{errorAlert && <div style={{ marginBottom: 16 }}>{errorAlert}</div>}
			{loading ? (
				<InlineLoading message={loadingMessage} />
			) : tabs ? (
				<Tabs activeKey={activeTab} onChange={onTabChange} items={tabs} />
			) : (
				children
			)}
		</>
	);

	// headerTone="primary": a plain AntD header (near-black text) is invisible/
	// wrong on an mg-primary background. These rules mirror the three-rule
	// hover/active pattern the Xoe chat header (xoe.css) uses (duplicated, not
	// shared, since this prop is the only coupling between the two), plus a
	// disabled overlay — header buttons set their own inline on-primary color,
	// which beats AntD's disabled class colour, so disabled needs !important
	// too — and the same treatment for AntD's own close icon, which this
	// component renders but never otherwise themes.
	return (
		<>
			{headerTone === "primary" && (
				<style>{`
					.mg-detail-drawer-header--primary .ant-btn-text { color: var(--mg-text-on-primary); }
					.mg-detail-drawer-header--primary .ant-btn-text:hover { background: var(--mg-primary-hover) !important; color: var(--mg-text-on-primary) !important; }
					.mg-detail-drawer-header--primary .ant-btn-text:active { background: var(--mg-primary-active) !important; }
					.mg-detail-drawer-header--primary .ant-btn-text:disabled { color: rgba(255, 255, 255, 0.45) !important; }
					.mg-detail-drawer-header--primary .ant-drawer-close { color: var(--mg-text-on-primary); }
					.mg-detail-drawer-header--primary .ant-drawer-close:hover { background: var(--mg-primary-hover) !important; color: var(--mg-text-on-primary) !important; }
					.mg-detail-drawer-header--primary .ant-drawer-close:active { background: var(--mg-primary-active) !important; }
				`}</style>
			)}
			<Drawer
				title={drawerTitle}
				placement="right"
				open={open}
				onClose={onClose}
				width={width}
				classNames={headerTone === "primary" ? { header: "mg-detail-drawer-header--primary" } : undefined}
				styles={{
					header:
						headerTone === "primary"
							? { background: "var(--mg-primary)", color: "var(--mg-text-on-primary)" }
							: undefined,
					body: isSidebar || noPadding ? { padding: 0, overflow: "hidden" } : { padding: 16 },
				}}
				footer={drawerFooter}
			>
				{body}
			</Drawer>
		</>
	);
};

export default DetailDrawer;
